// PlatformPages.tsx — 5 super_admin LIFF pages.
//
// All pages mount under <RoleGate allow={["super_admin"]}> and call the
// platform-scoped tenant-api endpoints. Server-side, every endpoint
// reverifies super_admin via nexus_control.super_admins (defense-in-depth).
// Frontend gating only suppresses the 403 flash for UX.

import React, { useEffect, useState } from "react";
import { useLiff } from "../../../liff/index.js";
import { apiFetch } from "../../../lib/api-client.js";
import {
  MobileShell, Card, CardHeader, MobileSpinner, EmptyMobile, ListRow,
  PrimaryButton, ComingSoonCard, MetricGrid, ProgressBar, StatusDot,
} from "../../../liff/components/Mobile.js";
import { formatTaipeiDateTime } from "../../../lib/time.js";

interface TenantRow { id: string; name: string; tenant_kind: "production" | "demo" | "platform"; users: number; sites: number }
interface TenantsResponse { items: TenantRow[]; current_id?: string | null }

/** /liff/platform/tenant-switcher — pick which tenant the rest of the app sees. */
export function PlatformTenantSwitcherPage(): React.ReactElement {
  const { session, refresh } = useLiff();
  const [data, setData] = useState<TenantsResponse | null>(null);
  const [switching, setSwitching] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/platform/tenants`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) setData(await r.json() as TenantsResponse);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  async function switchTo(tenantId: string): Promise<void> {
    if (!session) return;
    setSwitching(tenantId);
    setError(null);
    try {
      const r = await apiFetch(
        "/v1/platform/tenants/switch",
        { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ tenant_id: tenantId }) },
        { jwt: session.session_token },
      );
      if (r.ok) {
        await refresh();
        setData((d) => d ? { ...d, current_id: tenantId } : d);
      } else {
        setError(`切換失敗 HTTP ${r.status}`);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSwitching(null);
    }
  }

  if (error && !data) return <MobileShell title="切換租戶"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入租戶列表" />;

  return (
    <MobileShell title="切換租戶" subtitle={`${data.items.length} 個租戶`}>
      {error && <Card><p className="text-sm text-danger">{error}</p></Card>}
      <Card>
        <CardHeader title="可用租戶" />
        {data.items.length === 0 ? (
          <EmptyMobile title="沒有可切換的租戶" />
        ) : (
          <ul className="space-y-2">
            {data.items.map((t) => {
              const isCurrent = t.id === data.current_id;
              const tone = t.tenant_kind === "production" ? "text-success" : t.tenant_kind === "demo" ? "text-warning" : "text-accent";
              return (
                <li key={t.id}>
                  <ListRow
                    label={t.name}
                    sublabel={`${t.users} 名成員 · ${t.sites} 案場 · ${t.tenant_kind}`}
                    onClick={() => switchTo(t.id)}
                    disabled={isCurrent || switching === t.id}
                    accessory={
                      isCurrent ? <span className={`text-[10px] uppercase font-semibold ${tone}`}>當前</span>
                      : switching === t.id ? <span className="text-[10px] uppercase text-text-muted">切換中…</span>
                      : <span className="text-text-muted text-xs">→</span>
                    }
                  />
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </MobileShell>
  );
}

interface AnnouncementRow { id: string; at_local: string; severity: "info" | "warn" | "alert"; title: string; body: string }
interface AnnouncementsResponse { items: AnnouncementRow[] }

/** Backend /v1/platform/announcements real shape. */
interface BackendAnnouncementRow {
  id: string;
  title: string;
  body: string;
  audience: string;
  created_by: string | null;
  created_at: number;          // unix ms
  sent_at: number | null;
  reach_count: number | null;
}
function reshapeAnnouncements(rows: BackendAnnouncementRow[]): AnnouncementRow[] {
  return rows.map((r) => ({
    id: r.id,
    title: r.title,
    body: r.body,
    at_local: formatTaipeiDateTime(r.created_at ?? null),
    // Audience → severity color mapping. Platform doesn't model severity yet
    // (PR-pending), so all current rows fall to 'info'. Reserved for future.
    severity: "info",
  }));
}

/** /liff/platform/announcements — global system announcements. */
export function PlatformAnnouncementsPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<AnnouncementsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/platform/announcements`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as { items: BackendAnnouncementRow[] };
          setData({ items: reshapeAnnouncements(body.items ?? []) });
        } else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return <MobileShell title="系統公告"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入系統公告" />;

  return (
    <MobileShell title="系統公告" subtitle="平台層 · 全租戶">
      <Card>
        <CardHeader title="近期公告" hint={`${data.items.length} 筆`} />
        {data.items.length === 0 ? (
          <EmptyMobile title="目前沒有公告" />
        ) : (
          <ul className="space-y-2">
            {data.items.map((a) => {
              const tone = a.severity === "alert" ? "text-danger" : a.severity === "warn" ? "text-warning" : "text-text-secondary";
              return (
                <li key={a.id} className="rounded-xl bg-bg-raised p-3">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className="text-sm font-medium truncate">{a.title}</p>
                    <span className={`text-[10px] uppercase tracking-wider font-semibold ${tone}`}>{a.severity}</span>
                  </div>
                  <p className="text-[11px] text-text-muted mb-2">{a.at_local}</p>
                  <p className="text-xs text-text-secondary leading-relaxed line-clamp-3">{a.body}</p>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <ComingSoonCard
        title="發佈新公告"
        description="支援預排發佈、分群發送（平台 / 特定租戶）與 LINE Flex 樣式預覽。"
        willShow={["Markdown 編輯器", "預排發佈", "目標篩選（租戶 / 角色 / 案場）", "LINE Flex 預覽 + 統計開信率"]}
      />
    </MobileShell>
  );
}

interface PlatformReportsResponse { tenants: number; sites: number; incidents_today: number; uptime_30d: number }

/** /liff/platform/reports — cross-tenant report rollup. */
export function PlatformReportsPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<PlatformReportsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/platform/reports/summary`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) setData(await r.json() as PlatformReportsResponse);
        else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return <MobileShell title="跨租戶報表"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入跨租戶報表" />;

  return (
    <MobileShell title="跨租戶報表" subtitle="平台彙總">
      <Card>
        <CardHeader title="平台規模" hint="即時" />
        <MetricGrid items={[
          { label: "租戶數",     value: String(data.tenants) },
          { label: "案場數",     value: String(data.sites) },
          { label: "今日事件",   value: String(data.incidents_today), tone: data.incidents_today > 0 ? "warning" : "success" },
          { label: "30 天可用率", value: `${(data.uptime_30d * 100).toFixed(2)}%`, tone: data.uptime_30d >= 0.999 ? "success" : "warning" },
        ]} />
      </Card>
      <ComingSoonCard
        title="跨租戶儀表板"
        description="租戶間漏巡 / 事件 / SLA 比較、付費儀表板、客戶健康度評分。"
        willShow={["租戶 KPI 排行榜", "客戶健康度評分（含 trend）", "付費 + 計費對齊試算", "匯出 CSV / PDF"]}
      />
    </MobileShell>
  );
}

interface HealthCheck { name: string; ok: boolean; detail?: string; latency_ms?: number }
interface HealthResponse { checks: HealthCheck[]; overall_ok: boolean; checked_at_local: string }

/** Backend /v1/platform/health real shape (different from the LIFF-friendly
 *  HealthResponse above). Reshape locally for the mobile checks list. */
interface BackendHealthResponse {
  tenants_active: number;
  rps_1h: number;
  line_quota: { date_utc: string; sent_today: number; monthly_cap: number };
  probes: { service: string; status: "ok" | "warn" | "down" | "unknown"; detail?: string }[];
  shards: { name: string; kind: string; rows_approx: number | null }[];
  queues: { name: string; depth: number; note?: string }[];
}

function reshapeHealth(b: BackendHealthResponse): HealthResponse {
  const checks: HealthCheck[] = [];
  checks.push({ name: "Tenants（active）", ok: true, detail: String(b.tenants_active) });
  checks.push({ name: "RPS (近 1h)", ok: true, detail: `${b.rps_1h}/s` });
  const quotaPct = b.line_quota.monthly_cap > 0 ? b.line_quota.sent_today / b.line_quota.monthly_cap : 0;
  checks.push({
    name: "LINE 配額（今日 / 月上限）",
    ok: quotaPct < 0.8,
    detail: `${b.line_quota.sent_today} / ${b.line_quota.monthly_cap}`,
  });
  for (const p of b.probes) {
    checks.push({ name: `service: ${p.service}`, ok: p.status === "ok", detail: p.detail ?? p.status });
  }
  for (const s of b.shards) {
    checks.push({ name: `shard: ${s.name}`, ok: true, detail: s.rows_approx === null ? s.kind : `${s.kind} · ~${s.rows_approx} rows` });
  }
  for (const q of b.queues) {
    checks.push({ name: `queue: ${q.name}`, ok: true, detail: q.note ?? `depth ${q.depth}` });
  }
  const overall_ok = checks.every((c) => c.ok);
  return {
    checks,
    overall_ok,
    checked_at_local: formatTaipeiDateTime(new Date()),
  };
}

/** /liff/platform/health — operator health-at-a-glance. */
export function PlatformHealthPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/platform/health`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as BackendHealthResponse;
          setData(reshapeHealth(body));
        } else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  if (error) return <MobileShell title="平台健康度"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="檢查平台健康度" />;

  return (
    <MobileShell title="平台健康度" subtitle={data.checked_at_local || "—"}>
      <Card className="!p-3">
        <div className="flex items-center justify-between">
          <span className={`text-sm font-semibold ${data.overall_ok ? "text-success" : "text-danger"}`}>
            {data.overall_ok ? "全部正常" : "有異常"}
          </span>
          <StatusDot ok={data.overall_ok} label={data.overall_ok ? "Healthy" : "Degraded"} />
        </div>
      </Card>

      <Card>
        <CardHeader title="檢查項目" hint={`${data.checks.length} 項`} />
        {data.checks.length === 0 ? (
          <EmptyMobile title="暫無資料" />
        ) : (
          <ul className="space-y-2">
            {data.checks.map((c) => (
              <li key={c.name} className="rounded-xl bg-bg-raised p-3 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{c.name}</p>
                  {c.detail && <p className="text-[11px] text-text-muted truncate">{c.detail}</p>}
                </div>
                <div className="text-right">
                  <p className={`text-xs font-semibold ${c.ok ? "text-success" : "text-danger"}`}>{c.ok ? "OK" : "FAIL"}</p>
                  {c.latency_ms !== undefined && <p className="text-[10px] text-text-muted">{c.latency_ms} ms</p>}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </MobileShell>
  );
}

interface PlatformUserRow {
  id: string;
  display_name: string | null;   // backend returns nullable
  email?: string | null;
  /** Primary membership shown in the mobile UI; backend has more in
   *  `memberships[]` but mobile flattens to first. */
  role: string;
  tenant_id: string;
  /** When the row has > 1 membership we surface the count for context. */
  membership_count: number;
  super_admin: boolean;
  last_login_at: number | null;
  bound_via: "email" | "line" | "both";
}
interface PlatformUsersResponse { items: PlatformUserRow[]; total: number }

/** Backend /v1/platform/users real response. */
interface BackendPlatformUserRow {
  id: string;
  email: string | null;            // LINE-only members may have null email
  display_name: string | null;
  super_admin: boolean;
  last_login_at: number | null;
  memberships: { tenant_id: string; role: string; status: string }[];
  bound_via?: "email" | "line" | "both";
  line_user_id?: string | null;
}
interface BackendPlatformUsersResponse {
  items: BackendPlatformUserRow[];
  correlation_id?: string | null;
}

function reshapeUsers(b: BackendPlatformUsersResponse): PlatformUsersResponse {
  const items: PlatformUserRow[] = (b.items ?? []).map((u) => {
    // Pick first membership; super_admin without any tenant membership shows
    // role='super_admin' / tenant_id='_platform'.
    const primary = u.memberships?.[0];
    return {
      id: u.id,
      display_name: u.display_name ?? u.email ?? null,
      email: u.email,
      role: u.super_admin ? "super_admin" : (primary?.role ?? "—"),
      tenant_id: u.super_admin ? "_platform" : (primary?.tenant_id ?? "—"),
      membership_count: u.memberships?.length ?? 0,
      super_admin: u.super_admin,
      last_login_at: u.last_login_at,
      bound_via: u.bound_via ?? "email",
    };
  });
  return { items, total: items.length };
}

/** /liff/platform/users — cross-tenant user directory. */
export function PlatformUsersPage(): React.ReactElement {
  const { session } = useLiff();
  const [data, setData] = useState<PlatformUsersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!session) return;
    apiFetch(`/v1/platform/users?limit=50`, {}, { jwt: session.session_token })
      .then(async (r) => {
        if (r.ok) {
          const body = await r.json() as BackendPlatformUsersResponse;
          setData(reshapeUsers(body));
        } else setError(`HTTP ${r.status}`);
      })
      .catch((e) => setError((e as Error).message));
  }, [session]);

  const filtered = data?.items.filter((u) => {
    if (!q) return true;
    const needle = q.toLowerCase();
    return (
      (u.display_name ?? "").toLowerCase().includes(needle) ||
      (u.email ?? "").toLowerCase().includes(needle) ||
      u.tenant_id.toLowerCase().includes(needle)
    );
  }) ?? [];

  if (error) return <MobileShell title="使用者全表"><Card><p className="text-sm text-danger">無法載入：{error}</p></Card></MobileShell>;
  if (!data) return <MobileSpinner message="載入使用者全表" />;

  return (
    <MobileShell title="使用者全表" subtitle={`共 ${data.total} 人`}>
      <Card>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="搜尋名稱 / email / tenant"
          className="h-12 w-full rounded-xl bg-bg-raised px-3 text-sm placeholder:text-text-muted text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/40"
        />
      </Card>

      <Card>
        <CardHeader title="使用者" hint={`${filtered.length} 筆`} />
        {filtered.length === 0 ? (
          <EmptyMobile title="查無符合" />
        ) : (
          <ul className="space-y-2">
            {filtered.map((u) => (
              <li key={u.id} className="rounded-xl bg-bg-raised p-3">
                <div className="flex items-center justify-between gap-2 mb-1">
                  <p className="text-sm font-medium truncate">{u.display_name ?? u.email ?? u.id.slice(0, 8)}</p>
                  <div className="flex items-center gap-1 shrink-0">
                    {u.bound_via === "line" && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-success/15 text-success">LINE</span>
                    )}
                    {u.bound_via === "both" && (
                      <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/15 text-accent">EMAIL+LINE</span>
                    )}
                    <span className={`text-[10px] uppercase font-semibold ${u.super_admin ? "text-accent" : "text-text-muted"}`}>
                      {u.role}
                    </span>
                  </div>
                </div>
                <p className="text-[11px] text-text-muted truncate">
                  {u.email ?? "（LINE 綁定，未註冊 email）"}
                  {u.tenant_id !== "—" && <> · tenant {u.tenant_id.slice(0, 8)}…</>}
                  {u.membership_count > 1 && <> · 共 {u.membership_count} 個 membership</>}
                </p>
                <p className="text-[10px] text-text-muted">
                  {u.last_login_at
                    ? `最近登入 ${formatTaipeiDateTime(u.last_login_at)}`
                    : u.bound_via === "line" ? "—" : "未曾登入"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </MobileShell>
  );
}
